/**
 * What hovering a marker shows: an AI flag keeps its text tooltip, and a note
 * drives the rail's scrub preview — its captured frame when it has one, else
 * the video at its time.
 */
import type { Marker } from '../lib/buildMarkers';
import type { PreviewSnapshot } from './FrameSnapshot';
import type { MarkerPreview } from './Track';

const AI_KIND = 'ai';

/** The marker under the pointer (or keyboard focus), placed along the row. */
export interface MarkerHover {
  marker: Marker;
  leftPx: number;
}

export function isAiMarker(marker: Marker): boolean {
  return marker.kind === AI_KIND;
}

function hasCapturedFrame(marker: Marker): boolean {
  return !!marker.frameFileId || !!marker.localFrameUrl;
}

function snapshotOf(marker: Marker, entryId: string): PreviewSnapshot | undefined {
  if (!hasCapturedFrame(marker)) return undefined;
  return { entryId, fileId: marker.frameFileId, localUrl: marker.localFrameUrl };
}

/** What a hovered note puts in the rail's preview; an AI flag puts nothing there. */
export function markerPreviewFor(hover: MarkerHover | null, entryId: string): MarkerPreview | null {
  if (!hover || isAiMarker(hover.marker)) return null;
  const { marker, leftPx } = hover;
  return { tMs: marker.tMs, leftPx, snapshot: snapshotOf(marker, entryId), note: { author: marker.author, snippet: marker.snippet } };
}

/** The hovered AI flag, whose text tooltip the marker row draws itself. */
export function aiTooltipOf(hover: MarkerHover | null): MarkerHover | null {
  if (!hover || !isAiMarker(hover.marker)) return null;
  return hover;
}

/** A marker removed under the pointer never fires `mouseleave`, so its hover must not outlive it. */
export function liveHoverOf(hover: MarkerHover | null, markers: Marker[]): MarkerHover | null {
  if (!hover) return null;
  if (!markers.some((marker) => marker.id === hover.marker.id)) return null;
  return hover;
}
